import { Box, Checkbox, Chip, ListItemText, MenuItem, TextField } from "@mui/material";
import type { ReactNode } from "react";
import { useFilterDropdown } from "../hooks/useFilterDropdown";
import type { Tag } from "../lib/types";

type TagFilterSelectProps = {
  tags: Tag[];
  value: string[];
  onChange: (tagIds: string[]) => void;
  className?: string;
  label?: string;
  size?: "small" | "medium";
  startAdornment?: ReactNode;
};

export function TagFilterSelect({
  tags,
  value,
  onChange,
  className,
  label = "Tags",
  size = "small",
  startAdornment,
}: TagFilterSelectProps) {
  const dropdown = useFilterDropdown();
  const availableTags = tags.filter((tag) => !tag.is_archived || value.includes(tag.id));
  const selectedTags = value
    .map((tagId) => tags.find((tag) => tag.id === tagId))
    .filter((tag): tag is Tag => Boolean(tag));
  const emptyLabel = availableTags.length ? "All tags" : "No tags";

  return (
    <TextField
      select
      className={`tag-filter-select${className ? ` ${className}` : ""}`}
      size={size}
      label={label}
      value={value}
      slotProps={{
        input: { startAdornment },
        inputLabel: { shrink: true },
        select: {
          multiple: true,
          displayEmpty: true,
          open: dropdown.open,
          onOpen: dropdown.onOpen,
          onClose: dropdown.onClose,
          MenuProps: dropdown.MenuProps,
          renderValue: () =>
            selectedTags.length ? (
              <Box className="tag-filter-value">
                {selectedTags.map((tag) => (
                  <Chip key={tag.id} label={tag.name} size="small" />
                ))}
              </Box>
            ) : (
              <span className="tag-filter-placeholder">{emptyLabel}</span>
            ),
        },
      }}
      onChange={(event) => {
        const nextValue = event.target.value;
        onChange(typeof nextValue === "string" ? nextValue.split(",").filter(Boolean) : nextValue);
      }}
    >
      {!availableTags.length ? <MenuItem disabled>No tags</MenuItem> : null}
      {availableTags.map((tag) => (
        <MenuItem key={tag.id} value={tag.id}>
          <Checkbox checked={value.includes(tag.id)} size="small" />
          <span className="tag-filter-color" style={{ backgroundColor: tag.color }} aria-hidden="true" />
          <ListItemText primary={tag.name} />
        </MenuItem>
      ))}
    </TextField>
  );
}
